import { Button } from 'react-bootstrap';
import { LoadingIcon, SuccessModalIcon } from '../../../assets/Icon';
import Modal from '../../common/modal/Modal';
import styles from './style.module.scss';
import useAPI from '../../../hooks/useAPI';
import { useContext, useEffect, useRef, useState } from 'react';
import AuthContext from '../../../contexts/AuthContext';
import { URole } from '../../../constants/navigation';
import PendingRequestsContext from '../../../contexts/PendingRequestsProvider';
import TokenListContext from '../../../contexts/TokenListContext';
import TransactionSuccessModal from '../transaction-success-dialog';
import Loader from '../../common/Loader';
import WalletContext from '../../../contexts/WalletContext';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';

type TransactionSuccessModalProps = {
  open: boolean;
  onClose: () => void;
};

const ManageReceiveRequestModal = ({
  onClose,
  open,
}: TransactionSuccessModalProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const action = useRef<string | null>(null);
  const { userData: user, refetchAdminBalance } = useContext(AuthContext);
  const { tokenList } = useContext(TokenListContext);
  const {
    requestDetails,
    clearRequest,
    setToggleModal,
    processNextInQueue,
    currentNotificationId,
  } = useContext(PendingRequestsContext);
  const { refetchTransactions, refetch: refetchWalletBalance } =
    useContext(WalletContext);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const tokenId = tokenList?.[0]?.tokenId;

  // Helper to mark notification as seen
  const markNotificationAsSeenWithId = async (
    notificationId: string | null | undefined,
  ) => {
    if (!notificationId) return;
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (process.env.REACT_APP_NOTIFICATION_API_KEY) {
        headers.apiKey = process.env.REACT_APP_NOTIFICATION_API_KEY;
      }
      await fetch(
        `${process.env.REACT_APP_NOTIFICATION_ENGINE_API}/notifications/${notificationId}`,
        {
          method: 'PATCH',
          headers,
          body: JSON.stringify({ seen: true }),
        },
      );
    } catch (error) {
      // Handle error silently
    }
  };

  // API to mark notification as seen
  const { isLoading, refetch: manageRequest } = useAPI({
    method: 'POST',
    url:
      user?.role === URole.COMPANY_ADMIN
        ? '/token/admin/receive/accept'
        : '/token/user/receive/accept',
    onSuccess: () => {
      clearRequest();
      setSuccessModalOpen(true);
      refetchTransactions();
      refetchAdminBalance();
      refetchWalletBalance();
      navigate(location.pathname, { state: { lastRefreshedAt: +new Date() } });
    },
    onError: error => {
      toast.error(error?.response?.data?.message);
      setToggleModal(false);
      setSuccessModalOpen(false);
      clearRequest();
      setTimeout(() => processNextInQueue(), 100);
    },
  });

  const handleRequest = async (approveReject: boolean) => {
    try {
      if (!requestDetails?.callbackOptions) return;

      action.current = approveReject ? 'accepted' : 'rejected';

      // Mark notification as seen immediately when user takes action
      // Use currentNotificationId if available, otherwise use requestDetails._id
      const notificationId = currentNotificationId || requestDetails?._id;
      await markNotificationAsSeenWithId(notificationId);

      const { requestId, transactionId } = requestDetails?.callbackOptions;
      const payload: {
        checker: boolean;
        receiverId: string;
        transactionsId: string;
        createdId: string;
        tokenId?: string;
      } = {
        checker: approveReject,
        receiverId: user?.id as string,
        transactionsId: transactionId,
        createdId: requestId,
      };

      if (user?.role === URole.COMPANY_ADMIN) {
        payload.tokenId = tokenId;
      }
      manageRequest(payload);
    } catch (error) {}
  };

  return (
    <>
      <Modal
        open={open && !successModalOpen}
        onClose={() => {
          action.current = 'rejected';
          // Mark notification as seen if not already
          handleRequest(false);
        }}
        isCloseIconVisible={false}
      >
        <div className={styles.wrapper}>
          <h3 className="fw-bold">
            {user?.role === URole.COMPANY_ADMIN &&
            requestDetails?.callbackOptions?.role == 'ORGANIZATION'
              ? 'Mint Token and Receive Request'
              : ' Receive Request '}
          </h3>
          <h5 className={`${styles.title}`}>{requestDetails?.eventMessage}</h5>
          <p> Description : {requestDetails?.callbackOptions?.description}</p>
          <div className={styles.actions}>
            <Button
              variant="outline"
              type="button"
              className="border-black border-1"
              disabled={isLoading}
              onClick={() => {
                action.current = 'rejected';
                handleRequest(false);
              }}
            >
              {isLoading && action.current === 'rejected' && <Loader />}
              Reject
            </Button>
            <Button
              disabled={isLoading}
              variant="dark"
              type="submit"
              onClick={() => {
                action.current = 'accepted';
                handleRequest(true);
              }}
            >
              {isLoading && action.current === 'accepted' && <Loader />}
              Accept
            </Button>
          </div>
        </div>
      </Modal>
      <TransactionSuccessModal
        open={successModalOpen}
        title={`Request ${action.current} successfully`}
        isCloseIconVisible={true}
        onClose={() => {
          setSuccessModalOpen(false);
          setToggleModal(false);
          setTimeout(() => processNextInQueue(), 100);
        }}
      />
    </>
  );
};

export default ManageReceiveRequestModal;
